import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import ReactGA from 'react-ga';
import {
	isEqual, get, set, cloneDeep, pick,
} from 'lodash';
import moment from 'moment';
import Actions, { thunks } from '@redux/reports';
import { reportTypes } from '@config';

const Context = React.createContext(null);
const { Consumer } = Context;

class Provider extends PureComponent {
	constructor(props) {
		super(props);

		this.state = {
			form : {
				coordinatorId : '',
				officerId     : '',
				locationId    : '',
				fromDate      : moment().format('M/D/YYYY'),
				toDate        : moment().format('M/D/YYYY'),
			},
		};

		this.initialState = cloneDeep(this.state);

		props.getFilterData();
	}

	componentWillUnmount = () => this.clearFilters();

	changeReportType = (selection) => {
		const { updateStoreProperties, getFilterData, resetReportsListData } = this.props;
		resetReportsListData();
		this.setState(this.initialState);
		getFilterData();
		updateStoreProperties({ reportType : selection.value });
	}

	onFilterChange = (property) => (selection) => {
		const {
			resetAndCallOfficers, resetAndCallLocations, getLocationsData, getOfficersData, reportType,
		} = this.props;
		const newState = cloneDeep(this.state);
		const { form } = this.state;

		// handle DateTimePicker value or Select value
		const value = (property === 'fromDate' || property === 'toDate') ? selection : get(selection, 'value', '');

		// Update/Clear officers and locations if coordinator changes
		if (property === 'coordinatorId' && !isEqual(form[property], value)) {
			if (value) {
				resetAndCallOfficers(value);
				resetAndCallLocations(value, undefined);
			} else {
				set(newState, ['form', 'officerId'], '');
				set(newState, ['form', 'locationId'], '');
				getOfficersData();
				getLocationsData();
			}
		}

		// Update/Clear locations if officer changes
		if (property === 'officerId' && !isEqual(form[property], value)) {
			if (value) resetAndCallLocations(undefined, value);
			else {
				getLocationsData();
				set(newState, ['form', 'locationId'], '');
			}
		}

		set(newState, ['form', property], value);

		ReactGA.event({
			category : 'Reports',
			action   : `Changed ${reportTypes[reportType].label} Filter`,
			label    : property,
		});

		this.setState(newState);
	}

	clearFilters = () => {
		const { getFilterData, updateStoreProperties, resetReportsListData } = this.props;
		updateStoreProperties({ reportType : '' });
		resetReportsListData();
		this.setState(this.initialState);
		getFilterData();
	}

	applyFilters = () => {
		const { applyFilterGetReportData, reportType } = this.props;
		const { form } = this.state;

		ReactGA.event({
			category : 'Reports',
			action   : 'Pulled Report',
			label    : reportTypes[reportType].label,
		});

		applyFilterGetReportData(form);
	}

	render() {
		const {
			reportType, loading, selectOptions, children,
		} = this.props;
		return (
			<Context.Provider
				value={{
					...this.state,
					reportType,
					loading,
					selectOptions,
					applyFilters     : this.applyFilters,
					changeReportType : this.changeReportType,
					onFilterChange   : this.onFilterChange,
					clearFilters     : this.clearFilters,
					selectors        : get(reportTypes, [reportType, 'selectors'], []),
				}}
			>
				{ children }
			</Context.Provider>
		);
	}
}

const mapStateToProps = ({
	reports : {
		reportType,
		loading,
		data : selectOptions,
	},
}) => ({
	reportType,
	loading,
	selectOptions,
});

const mapDispatchToProps = (dispatch) => ({
	...pick(bindActionCreators(thunks, dispatch), [
		'getLocationsData',
		'getOfficersData',
		'applyFilterGetReportData',
		'getFilterData',
		'resetAndCallOfficers',
		'resetAndCallLocations',
	]),
	...pick(bindActionCreators(Actions, dispatch), ['resetReportsListData', 'updateStoreProperties']),
});

Provider.propTypes = {
	applyFilterGetReportData : PropTypes.func.isRequired,
	children                 : PropTypes.any,
	getFilterData            : PropTypes.func.isRequired,
	getLocationsData         : PropTypes.func.isRequired,
	getOfficersData          : PropTypes.func.isRequired,
	loading                  : PropTypes.object.isRequired,
	reportType               : PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number,
	]),
	resetAndCallLocations : PropTypes.func.isRequired,
	resetAndCallOfficers  : PropTypes.func.isRequired,
	resetReportsListData  : PropTypes.func.isRequired,
	selectOptions         : PropTypes.object,
	updateStoreProperties : PropTypes.func.isRequired,
};

Provider.defaultProps = {
	children      : undefined,
	reportType    : '',
	selectOptions : {},
};

export default {
	Provider : connect(mapStateToProps, mapDispatchToProps)(Provider),
	Consumer,
};
